import { AuthState } from '../../hooks/useAuthState'
import { IthacaOperationName } from '@dtx-company/true-common/src/types/operations'
import { NextApiRequest, NextApiResponse } from 'next'
import { OrgRoleEnum } from '@dtx-company/true-common/src/types/org'
import { Routes } from '@dtx-company/true-common/src/constants/routes'
import jwt_decode, { JwtPayload } from 'jwt-decode'

export interface AuthorizeUserByRequestArgs {
  req?: NextApiRequest
  nameOfAuthorizedOrg?: string
  authorizedOrgRoles?: OrgRoleEnum[]
  unauthorizedOrgRoles?: OrgRoleEnum[]
  authorizedExtraOrgRoles?: string[]
  unauthorizedExtraOrgRoles?: string[]
  authorizedOperation?: IthacaOperationName
  shouldBeOrgMember?: boolean
  jwt: AuthState['jwt']
}

export const isUserAuthorizedToViewPage = (args: AuthorizeUserByRequestArgs): boolean => {
  try {
    let userBelongsToAuthorizedOrg = true
    let userHasNecessaryRoles = true
    let userHasNecessaryExtraRoles = true
    let userHasNecessaryOperation = true

    const jwt = args.jwt

    if (jwt) {
      const now = Math.round(Date.now() / 1000)
      const decodedToken = jwt_decode<JwtPayload>(jwt.token)
      const tokenExpired = decodedToken.exp && decodedToken.exp <= now

      if (!tokenExpired) {
        if (!jwt?.org && !args.nameOfAuthorizedOrg && !args.shouldBeOrgMember) return true

        if (jwt?.org) {
          const userOrg = jwt?.org

          if (args?.nameOfAuthorizedOrg) {
            userBelongsToAuthorizedOrg = userOrg.name === args.nameOfAuthorizedOrg
          }

          if (args?.unauthorizedOrgRoles?.length) {
            userHasNecessaryRoles = args.unauthorizedOrgRoles.every(
              (unauthRole: OrgRoleEnum) => unauthRole !== userOrg.userOrgRole
            )
          }

          if (args?.authorizedOrgRoles?.length) {
            userHasNecessaryRoles = args.authorizedOrgRoles.some(
              (authRole: OrgRoleEnum) => authRole === userOrg.userOrgRole
            )
          }

          if (args?.unauthorizedExtraOrgRoles?.length) {
            userHasNecessaryExtraRoles =
              args.unauthorizedExtraOrgRoles.filter(unauthExtraRole =>
                userOrg.userOrgRoleExtras?.includes(unauthExtraRole)
              ).length === 0
          }

          if (args?.authorizedExtraOrgRoles?.length) {
            userHasNecessaryExtraRoles = Boolean(
              args.authorizedExtraOrgRoles.filter(authExtraRole =>
                userOrg.userOrgRoleExtras?.includes(authExtraRole)
              ).length
            )
          }

          if (args?.authorizedOperation) {
            userHasNecessaryOperation = Boolean(
              userOrg?.permissions?.some(perm => {
                return perm.operations.some(op => op === args.authorizedOperation)
              })
            )
          }

          return (
            userBelongsToAuthorizedOrg &&
            userHasNecessaryRoles &&
            userHasNecessaryExtraRoles &&
            userHasNecessaryOperation
          )
        }
      }
    }
    return false
  } catch (e) {
    console.error('error in authorizeUserByRequest: ', e)
    return false
  }
}

interface RedirectByContextType {
  res: NextApiResponse
  redirectTo: Routes
}

export const redirectByResponse = ({ res, redirectTo }: RedirectByContextType): void => {
  res.writeHead(302, { Location: redirectTo })
  res.end()
}

export interface ContextType {
  req: NextApiRequest
  res: NextApiResponse
}

export interface GetServerSidePropsReturnType {
  props: Record<string, never>
}
